import React, { useCallback } from 'react';
import { Box, Typography, Divider, Paper, Button, Grid2, IconButton, Tooltip, Popover, List, ListItem, ListItemText } from '@mui/material';
import { PackageDefinitionFormValues } from './PackageDefinition.helper';
import styles from '../../Billing/Package/PackageDetails/CounselingDetails/CounselingDetails.module.css';
import { ReactComponent as Edit } from '../../../assets/SVG/Edit.svg';
import { useNavigate } from 'react-router-dom';
import useCurrency from '../../../components/CurrencyProvider';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';
import TruncatedText from '../../../utils/TruncatedText';
import { useTranslation } from 'react-i18next';

// Reusable grid sizes
const GRID_SIZE = { xl: 3, lg: 3, md: 4, xs: 6 } as const;
const GRID_SIZE_QUARTER = { lg: 2.3, md: 3, xs: 6 } as const;

interface Props {
  data: PackageDefinitionFormValues;
  onBack?: () => void;
}

const PackageDefinitionViewMode: React.FC<Props> = ({ data, onBack }) => {
  const pd = data.package_definition;
  const navigate = useNavigate();
  const { currencySymbol } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  const [activeTab, setActiveTab] = React.useState<'services' | 'pharmacy'>('services');
  
  // Popover state for "Service From No. of Cycles" badge
  const [cyclePopoverAnchor, setCyclePopoverAnchor] = React.useState<{ element: HTMLElement | null; serviceIdx: number | null }>({
    element: null,
    serviceIdx: null,
  });

  const handleCyclePopoverOpen = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, serviceIdx: number) => {
    setCyclePopoverAnchor({ element: event.currentTarget, serviceIdx });
  };

  const handleCyclePopoverClose = () => {
    setCyclePopoverAnchor({ element: null, serviceIdx: null });
  };

  const handleCyclePopoverKeyDown = (event: React.KeyboardEvent<HTMLElement>, serviceIdx: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCyclePopoverOpen(event, serviceIdx);
    }
  };

  const toLabel = (value: unknown, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      const maybeName = (value as { name?: unknown }).name;
      if (typeof maybeName === 'string' || typeof maybeName === 'number' || typeof maybeName === 'boolean') {
        return String(maybeName);
      }
    }
    return fallback;
  };

  const formatCurrency = useCallback((value: unknown) => {
    return `${currencySymbol}${Number(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [currencySymbol]);

  const header = {
    packageName: toLabel(pd?.package_name),
    packageCode: toLabel(data?.package_code),
    packageAmount: Number(pd?.package_amount ?? 0),
    packageDuration: toLabel(pd?.package_duration),
    durationType: toLabel(pd?.duration_type),
    distributionType: toLabel(pd?.distribution_type),
    serviceAmount: Number(pd?.service_amount ?? 0),
    pharmacyAmount: Number(pd?.pharmacy_amount ?? 0),
    servicePercent: Number(pd?.service_pct ?? pd?.service_percent ?? 0),
    pharmacyPercent: Number(pd?.pharmacy_pct ?? pd?.pharmacy_percent ?? 0),
    concessionAmount: Number(pd?.package_concession_amount ?? 0),
    netAmount: Number(pd?.package_net_amount ?? 0),
    remark: pd?.remark ?? '-',
    finalized: pd?.is_finalized,
    adjustable_head: pd?.adjustable_head,
    is_consumables: pd?.is_consumables,
    consumable_amount: Number(pd?.consumable_amount ?? 0),
    procedure: pd?.procedure_details?.length
      ? pd.procedure_details.map((d: { procedure_source_name?: string }) => d.procedure_source_name || '').filter(Boolean).join(', ')
      : (pd?.procedure_source_name ?? ''),
  };

  // Pre-compute all service calculations
  const processedServices = React.useMemo(() => {
    const servicesRaw = Array.isArray(data.service_items) ? data.service_items.filter(s => s.service && s.service_name) : [];
    return servicesRaw.map((s, idx) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sAny = s as any;
      const unitRate = Number(s.unit_rate) || 0;
      const quantity = Number(s.quantity) || 0;
      const grossAmount = unitRate * quantity;

      // Per-unit discount values from the service item
      const discountPerUnit = Number(s.discount_amount) || 0;
      const individualDiscountPerUnit = Number(sAny.individual_discount_amount ?? 0) || 0;
      const packageDiscountPerUnit = discountPerUnit - individualDiscountPerUnit;
      
      // Total discounts (per-unit * quantity)
      const totalDiscount = discountPerUnit * quantity;
      const manualDiscount = individualDiscountPerUnit * quantity;
      const packageConcession = packageDiscountPerUnit * quantity;
      
      // Discount percent based on totals
      const discountPercent = grossAmount > 0 ? (totalDiscount / grossAmount) * 100 : 0;
      const serviceAmount = Number(s.total_amount ?? 0);
      const isUnlimited = s.unlimited_qty;

      // Extract procedure_details from service item for "Service From No. of Cycles"
      const procedureDetails = (s as unknown as { procedure_details?: Array<{ procedure_id: number; procedure_source_name?: string }> }).procedure_details || [];
      const cycleCount = procedureDetails.length;
      const cycleNames = procedureDetails.map(pd => pd.procedure_source_name || '').filter(Boolean);

      return {
        ...s,
        idx,
        unitRate,
        quantity,
        grossAmount,
        discountAmount: discountPerUnit,
        individualDiscountAmount: individualDiscountPerUnit,
        totalDiscount,
        manualDiscount,
        cycleCount,
        cycleNames,
        packageConcession,
        discountPercent,
        serviceAmount,
        isUnlimited,
        quantityDisplay: isUnlimited ? 'Unlimited' : (s.quantity ?? '-'),
        // Pre-formatted strings
        grossAmountStr: formatCurrency(grossAmount),
        serviceAmountStr: formatCurrency(serviceAmount),
        totalDiscountStr: formatCurrency(totalDiscount),
        manualDiscountStr: formatCurrency(manualDiscount),
        packageConcessionStr: formatCurrency(packageConcession),
        discountPercentStr: discountPercent.toFixed(2),
        unitRateStr: formatCurrency(unitRate),
      };
    });
  }, [data.service_items, formatCurrency]);

  const totalServiceAmount = processedServices.reduce((sum: number, s) => sum + s.grossAmount, 0);

  // Pre-compute all pharmacy calculations
  const processedPharmacy = React.useMemo(() => {
    const pharmacyRaw = Array.isArray(data.pharmacy_items) ? data.pharmacy_items.filter(p => p.item && p.item_name) : [];
    return pharmacyRaw.map((p, idx) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pAny = p as any;
      const unitRate = Number(pAny.unit_rate ?? 0) || 0;
      const quantity = Number(p.quantity) || 0;
      const grossAmount = unitRate * quantity;

      // Per-unit discount values from the pharmacy item (if available)
      const discountPerUnit = Number(pAny.discount_amount ?? 0) || 0;
      const individualDiscountPerUnit = Number(pAny.individual_discount_amount ?? 0) || 0;
      const packageDiscountPerUnit = discountPerUnit - individualDiscountPerUnit;
      
      // Total discounts (per-unit * quantity)
      const totalDiscount = discountPerUnit * quantity;
      const manualDiscount = individualDiscountPerUnit * quantity;
      const packageConcession = packageDiscountPerUnit * quantity;
      
      const discountPercent = grossAmount > 0 ? (totalDiscount / grossAmount) * 100 : 0;
      const netAmount = Number(p.total_amount ?? 0);

      return {
        ...p,
        idx,
        unitRate,
        quantity,
        grossAmount,
        totalDiscount,
        manualDiscount,
        packageConcession,
        discountPercent,
        netAmount,
        // Pre-formatted strings
        grossAmountStr: formatCurrency(grossAmount),
        netAmountStr: formatCurrency(netAmount),
        totalDiscountStr: formatCurrency(totalDiscount),
        manualDiscountStr: formatCurrency(manualDiscount),
        packageConcessionStr: formatCurrency(packageConcession),
        discountPercentStr: discountPercent.toFixed(2),
        unitRateStr: formatCurrency(unitRate),
      };
    });
  }, [data.pharmacy_items, formatCurrency]);

  return (
    <Box sx={{ px: 3, py: 2 }} data-testid="package-definition-view-mode-container">
      <Box sx={{ display: 'flex', justifyContent: !header.finalized ? 'space-between' : 'flex-start', mb: 2 }}>
        <Typography
          variant="h6"
          className={styles.h6Title}
          sx={{ mb: 3 }}
        >
          {tBilling('package_config.package_details', { defaultValue: 'Package Details' })}
        </Typography>
      
        {!header.finalized && (
          <IconButton onClick={onBack}>
            <Edit />
          </IconButton>
        )}
      </Box>

      <Grid2 container spacing={2} sx={{ mb: 3 }}>
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>{tBilling('package_config.headers.package_name', { defaultValue: 'Package Name' })}</Typography>
          <Typography className={styles.value}>{header.packageName}</Typography>
        </Grid2>
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>{tBilling('package_config.headers.package_amount', { defaultValue: 'Package Amount' })} ({currencySymbol})</Typography>
          <Typography className={styles.value}>{formatCurrency(header.packageAmount)}</Typography>
        </Grid2>
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>{tBilling('package_config.package_duration', { defaultValue: 'Package Duration' })}</Typography>
          <Typography className={styles.value}>{header.packageDuration} {convertUnderscoreToTitle(header.durationType)}</Typography>
        </Grid2>
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>{tBilling('package_config.distribution_details', { defaultValue: 'Distribution Details' })}</Typography>
          <Typography className={styles.value}>{convertUnderscoreToTitle(header.distributionType)}</Typography>
        </Grid2>
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>
            {tBilling('package_details.service_amount', { defaultValue: 'Service Amount' })} ({header.servicePercent.toFixed(2)}%)
          </Typography>
          <Typography className={styles.value}>{formatCurrency(header.serviceAmount)}</Typography>
        </Grid2>
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>
            {tBilling('package_details.pharmacy_amount', { defaultValue: 'Pharmacy Amount' })} ({currencySymbol}) ({header.pharmacyPercent.toFixed(2)}%)
          </Typography>
          <Typography className={styles.value}>{formatCurrency(header.pharmacyAmount)}</Typography>
        </Grid2>
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.adjustable_head', { defaultValue: 'Adjustable Head' })}</Typography>
          <Typography className={styles.value}>{header.adjustable_head ? tBilling('Advance.yes', { defaultValue: 'Yes' }) : tBilling('Advance.no', { defaultValue: 'No' })}</Typography>
        </Grid2>
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.is_consumables', { defaultValue: 'Is Consumables' })}</Typography>
          <Typography className={styles.value}>{header.is_consumables ? tBilling('Advance.yes', { defaultValue: 'Yes' }) : tBilling('Advance.no', { defaultValue: 'No' })}</Typography>
        </Grid2>
        {header.is_consumables && (
          <Grid2 size={GRID_SIZE}>
            <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.consumable_amount', { defaultValue: 'Consumable Amount' })} ({currencySymbol})</Typography>
            <Typography className={styles.value}>{formatCurrency(header.consumable_amount)}</Typography>
          </Grid2>
        )}
        <Grid2 size={GRID_SIZE}>
          <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.remark', { defaultValue: 'Remark' })}</Typography>
          <Typography className={styles.value}>
            {header.remark && <TruncatedText text={header.remark as string} title={tBilling('package_details.remark', { defaultValue: 'Remark' })} iconPosition="left" />}
          </Typography>
        </Grid2>
      </Grid2>

      <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

      {/* Services / Pharmacy Items Tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: 16,
            color: activeTab === 'services' ? '#e57353' : '#bdbdbd',
            borderColor: activeTab === 'services' ? '#e57353' : '#bdbdbd',
            backgroundColor: '#fff',
          }}
          onClick={() => setActiveTab('services')}
        >
          {tBilling('package_config.services', { defaultValue: 'Services' })}
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: 16,
            color: activeTab === 'pharmacy' ? '#e57353' : '#bdbdbd',
            borderColor: activeTab === 'pharmacy' ? '#e57353' : '#bdbdbd',
            backgroundColor: '#fff',
          }}
          onClick={() => setActiveTab('pharmacy')}
        >
          {tBilling('package_config.pharmacy_items', { defaultValue: 'Pharmacy Items' })}
        </Button>
      </Box>

      {/* Services Tab */}
      {activeTab === 'services' && (
        <Box>
          {header.procedure && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 2 }}>
              <Typography sx={{ fontSize: 14 }}>
                <Typography component="span" color="text.primary">{tBilling('package_config.cycle', { defaultValue: 'Cycle' })}: </Typography>
                {header.procedure}
              </Typography>
            </Box>
          )}
          {/* Services Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)', borderRadius: 2, px: 2.5, py: 1, mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#495057' }}>{tBilling('package_details.service_name', { defaultValue: 'Service Name' })}</Typography>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#495057' }}>
              {tBilling('package_details.total_amount', { defaultValue: 'Total Amount' })}: <Box component="span" sx={{ color: '#2e7d32' }}>{formatCurrency(totalServiceAmount)}</Box>
            </Typography>
          </Box>
          {/* Service Cards */}
          {processedServices.length === 0 ? (
            <Typography color="text.secondary">{tBilling('package_details.no_services', { defaultValue: 'No services.' })}</Typography>
          ) : (
            processedServices.map((s) => (
              <Paper 
                key={s.idx} 
                elevation={0}
                sx={{ borderRadius: 2, mb: 2, overflow: 'hidden', border: '1px solid #e0e0e0', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } }}
              >
                {/* Service Card Header */}
                <Box sx={{ bgcolor: '#fafafa', px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 24, bgcolor: '#e57353', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                      {s.service_name || `${tBilling('package_details.service', { defaultValue: 'Service' })} ${s.idx + 1}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Service From No. of Cycles badge */}
                    {s.cycleCount > 0 && (
                      <>
                        <Tooltip title={tBilling('package_details.click_to_see_cycle_details', { defaultValue: 'Click to see cycle details' })}>
                          <Box
                            role="button"
                            tabIndex={0}
                            aria-label={tBilling('package_details.click_to_see_cycle_details', { defaultValue: 'Click to see cycle details' })}
                            onClick={(e) => handleCyclePopoverOpen(e, s.idx)}
                            onKeyDown={(e) => handleCyclePopoverKeyDown(e, s.idx)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              bgcolor: '#e3f2fd',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#bbdefb' },
                              '&:focus': { outline: '2px solid #1976d2', outlineOffset: 2 },
                            }}
                          >
                            <Typography sx={{ fontSize: 12, color: '#1976d2', fontWeight: 500 }}>
                              {tBilling('package_details.service_from_cycles', { defaultValue: 'Service From No. of Cycles' })}:
                            </Typography>
                            <Box
                              sx={{
                                minWidth: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: '#1976d2',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {s.cycleCount}
                            </Box>
                          </Box>
                        </Tooltip>
                        <Popover
                          open={cyclePopoverAnchor.element !== null && cyclePopoverAnchor.serviceIdx === s.idx}
                          anchorEl={cyclePopoverAnchor.element}
                          onClose={handleCyclePopoverClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                          PaperProps={{
                            sx: {
                              backgroundColor: '#fff',
                              color: '#222',
                              borderRadius: 2,
                              minWidth: 200,
                              maxWidth: 320,
                              p: 1,
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            },
                          }}
                        >
                          <Typography sx={{ fontSize: 12, color: '#1976d2', px: 1, pt: 0.5 }}>
                            {tBilling('package_details.cycle_details', { defaultValue: 'CYCLE DETAILS' })} ({s.cycleCount})
                          </Typography>
                          <List dense sx={{ py: 0 }}>
                            {s.cycleNames.map((name: string, i: number) => (
                              <ListItem key={i} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={name}
                                  primaryTypographyProps={{ sx: { fontSize: 14, color: '#222' } }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Popover>
                      </>
                    )}
                    <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#444' }}>
                      {tBilling('package_details.service_amount_label', { defaultValue: 'Service Amount' })}: <Box component="span" sx={{ color: '#2e7d32', fontWeight: 600 }}>{s.serviceAmountStr}</Box>
                    </Typography>
                  </Box>
                </Box>
                
                {/* Service Card Body */}
                <Grid2 container sx={{ px: 2, py: 2 }} spacing={2}>
                  <Grid2 size={GRID_SIZE_QUARTER}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.procedure', { defaultValue: 'Procedure' })}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{convertUnderscoreToTitle(s.procedure) || '-'}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZE_QUARTER}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.gender', { defaultValue: 'Gender' })}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{toLabel(s.gender)}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZE_QUARTER}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.unit_rate', { defaultValue: 'Service Rate' })}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.unitRateStr}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZE_QUARTER}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.quantity', { defaultValue: 'Quantity' })}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.quantityDisplay}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZE_QUARTER}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.unit_percentage', { defaultValue: 'Unit Percentage' })} (%):</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{Number(s.unit_percentage ?? 0).toFixed(2)}</Typography>
                  </Grid2>
                </Grid2>
              </Paper>
            ))
          )}
        </Box>
      )}

      {/* Pharmacy Items Tab */}
      {activeTab === 'pharmacy' && (
        <Box>
          {pd?.pharmacy_set_all_items ? (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {tBilling('package_config.set_all_items_description', { defaultValue: 'Set All Items: All pharmacy items are available for this package.' })}
            </Typography>
          ) : processedPharmacy.length === 0 ? (
            <Typography color="text.secondary">{tBilling('package_config.no_pharmacy_items', { defaultValue: 'No pharmacy items.' })}</Typography>
          ) : (
            processedPharmacy.map((p) => (
              <Paper 
                key={p.idx} 
                elevation={0}
                sx={{ borderRadius: 2, mb: 2, overflow: 'hidden', border: '1px solid #e0e0e0', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } }}
              >
                {/* Pharmacy Card Header */}
                <Box sx={{ bgcolor: '#fafafa', px: 2.5, py: 1.25, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 24, bgcolor: '#e57353', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                      {p.item_name || `${tBilling('package_config.item', { defaultValue: 'Item' })} ${p.idx + 1}`}
                    </Typography>
                  </Box>

                </Box>
                {/* Pharmacy Card Body */}
                <Grid2 container sx={{ px: 2, py: 2 }} spacing={2}>
                  <Grid2 size={GRID_SIZE}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.quantity', { defaultValue: 'Quantity' })}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.quantity}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZE}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.uom', { defaultValue: 'UOM' })}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(() => {
                        const matchingConversion = p.uom_conversion?.find((c: { from_uom: number }) => c.from_uom === p.uom);
                        return matchingConversion?.from_uom_label || toLabel(p.uom_description ?? p.uom);
                      })()}
                    </Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZE}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.discount', { defaultValue: 'Discount' })} (%):</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{Number(p.discount_percent ?? 0).toFixed(2)}</Typography>
                  </Grid2>
                </Grid2>
              </Paper>
            ))
          )}
        </Box>
      )}

      {/* Back To List Button */}
      {header.finalized && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button 
            variant="outlined" 
            sx={{ minWidth: 160 }} 
            onClick={() => { 
              navigate('/ai/configuration/package-configuration/package', { state: { refetch: true, ts: Date.now() } }); 
            }}
          >
            {tBilling('package_config.back_to_list', { defaultValue: 'Back To List' })}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PackageDefinitionViewMode;
